/**
 * ImageUpload Component
 * Drag-and-drop image upload with preview
 */

import * as React from 'react';
import { cn } from '../../../lib/utils';
import { validateImageFile } from '../../../schemas/widgetConfigSchema';

export interface ImageUploadProps {
  value?: string;
  onChange: (file: File) => void;
  onRemove?: () => void;
  label?: string;
  description?: string;
  error?: string;
  accept?: string;
  maxSize?: number;
  recommendedDimensions?: string;
  isUploading?: boolean;
  className?: string;
}

/**
 * ImageUpload provides drag-and-drop file upload with preview
 * Supports file validation and visual feedback
 */
export function ImageUpload({
  value,
  onChange,
  onRemove,
  label,
  description,
  error,
  accept = 'image/png,image/jpeg,image/svg+xml,image/webp',
  maxSize = 5 * 1024 * 1024, // 5MB
  recommendedDimensions,
  isUploading = false,
  className,
}: ImageUploadProps) {
  const [isDragging, setIsDragging] = React.useState(false);
  const [validationError, setValidationError] = React.useState<string | undefined>();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileSelect = (file: File) => {
    setValidationError(undefined);

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setValidationError(validation.error);
      return;
    }

    onChange(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setValidationError(undefined);
    if (onRemove) {
      onRemove();
    }
  };

  const displayError = error || validationError;

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-900">
          {label}
        </label>
      )}
      {description && (
        <p className="text-sm text-gray-600">{description}</p>
      )}
      {recommendedDimensions && (
        <p className="text-xs text-gray-500">
          Recommended: {recommendedDimensions}
        </p>
      )}

      {/* Upload Area */}
      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={cn(
          'relative rounded-lg border-2 border-dashed transition-all cursor-pointer',
          'hover:border-primary-500 hover:bg-gray-50',
          isDragging && 'border-primary-500 bg-primary-50',
          displayError && 'border-red-300',
          value && 'border-gray-300',
          'focus-within:ring-2 focus-within:ring-primary-500'
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileInputChange}
          className="sr-only"
          aria-label={label || 'Upload image'}
          disabled={isUploading}
        />

        {value ? (
          /* Image Preview */
          <div className="relative p-4">
            <img
              src={value}
              alt="Preview"
              className="mx-auto max-h-48 rounded object-contain"
            />
            {onRemove && !isUploading && (
              <button
                type="button"
                onClick={handleRemove}
                className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-2 hover:bg-red-700 transition-colors"
                aria-label="Remove image"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        ) : (
          /* Upload Prompt */
          <div className="p-8 text-center">
            {isUploading ? (
              <div className="space-y-2">
                <div className="mx-auto w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-gray-600">Uploading...</p>
              </div>
            ) : (
              <>
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <p className="mt-2 text-sm text-gray-600">
                  <span className="font-semibold text-primary-600">
                    Click to upload
                  </span>{' '}
                  or drag and drop
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  PNG, JPG, SVG, or WebP up to {Math.round(maxSize / 1024 / 1024)}MB
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      {displayError && (
        <p className="text-sm text-red-600" role="alert">
          {displayError}
        </p>
      )}
    </div>
  );
}
